import { useState, useEffect, useRef, useCallback } from 'react';
import { authPost, authFetch } from '../lib/authFetch';
import { supabase } from '../lib/supabase';
import { isLocalModeEnabled, getLocalDonors, addLocalDonor, updateLocalDonor, getLocalFeedEntries, addLocalFeedEntry } from '../lib/localMode';

/**
 * Shared chat/feed logic hook used by both MobileApp and DesktopApp
 *
 * This hook encapsulates all the state and handlers for:
 * - Feed entries (loading, saving, streaming)
 * - Chat submission and AI responses
 * - Person adding flow (group selection, research prompts)
 * - List creation from chat
 * - Research flows
 */
export function useChatLogic({
  donors,
  groups,
  savedLists,
  savedTables,
  onRefreshDonors,
  onNavigateToGroup,
  onNavigateToPerson,
  onNavigateToList,
  onShowCreateGroupSheet,
  onOpenAddPersonModal,
  setLocalGroups,
  setLocalLists,
  setLocalTables,
  userContext, // User's saved context for AI personalization
  onRefreshUserContext // Callback to refresh user context after onboarding
}) {
  // Feed state
  const [feedEntries, setFeedEntries] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Streaming state
  const [streamingText, setStreamingText] = useState('');
  const [streamingEntryId, setStreamingEntryId] = useState(null);

  // Research flow state
  const [pendingResearch, setPendingResearch] = useState(null);
  const [, setPendingMoreInfo] = useState(null);
  const [narrativeUpdate, setNarrativeUpdate] = useState(null);

  // Track pending suggest_add so we can handle "yes" responses reliably
  const [pendingSuggestAdd, setPendingSuggestAdd] = useState(null);

  const feedLoadedRef = useRef(false);

  // Ref to hold handleConfirmAdd for use in handleSubmit (avoids circular dep)
  const handleConfirmAddRef = useRef(null);

  // ============================================================================
  // FEED LOADING & PERSISTENCE
  // ============================================================================

  // Load feed entries from database on mount - or from local storage only,
  // never Supabase, in Local Mode. Conversation history is data too: loading
  // it from the real account here would let earlier default-mode exchanges
  // (and whatever real donor details they contain) leak into Local Mode's
  // context, even though the donor list itself is correctly local-only.
  useEffect(() => {
    const loadFeedEntries = async () => {
      if (feedLoadedRef.current) return;
      feedLoadedRef.current = true;

      if (isLocalModeEnabled()) {
        setFeedEntries(getLocalFeedEntries());
        setFeedLoading(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { data, error } = await supabase
            .from('feed_entries')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(100);

          if (!error && data && data.length > 0) {
            const entries = data.map(row => ({
              id: row.id,
              type: row.type,
              message: row.message,
              donorName: row.donor_name,
              captured: row.captured || [],
              status: row.status,
              timestamp: row.created_at,
              listPreview: row.list_preview || null
            }));
            setFeedEntries(entries);
            localStorage.setItem('knowroom-feed', JSON.stringify(entries));
            return;
          }
        }

        const saved = localStorage.getItem('knowroom-feed');
        if (saved) {
          setFeedEntries(JSON.parse(saved));
        }
      } catch (e) {
        console.error('Failed to load feed:', e);
        const saved = localStorage.getItem('knowroom-feed');
        if (saved) {
          try {
            setFeedEntries(JSON.parse(saved));
          } catch (parseError) {
            console.error('Failed to parse localStorage feed:', parseError);
          }
        }
      } finally {
        setFeedLoading(false);
      }
    };

    loadFeedEntries();
  }, []);

  // Save feed entries to localStorage as cache - Local Mode uses its own
  // separate key so it never contaminates the default-mode cache, or vice versa.
  useEffect(() => {
    if (feedEntries.length === 0) return;
    if (isLocalModeEnabled()) {
      localStorage.setItem('knowroom-local-feed', JSON.stringify(feedEntries.slice(0, 100)));
    } else {
      localStorage.setItem('knowroom-feed', JSON.stringify(feedEntries.slice(0, 100)));
    }
  }, [feedEntries]);

  // Helper to save entry to database - or to local storage only in Local Mode,
  // so chat messages never reach the real account's feed_entries table.
  const saveFeedEntryToDb = useCallback(async (entry) => {
    if (isLocalModeEnabled()) {
      addLocalFeedEntry(entry);
      return;
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await supabase.from('feed_entries').upsert({
        id: entry.id,
        user_id: session.user.id,
        type: entry.type || 'note',
        message: entry.message,
        donor_name: entry.donorName || null,
        captured: entry.captured || [],
        status: entry.status || 'saved',
        created_at: entry.timestamp || new Date().toISOString(),
        list_preview: entry.listPreview || null
      }, { onConflict: 'id' });
    } catch (e) {
      console.error('Failed to save feed entry to db:', e);
    }
  }, []);

  // ============================================================================
  // REFRESH LISTS & TABLES
  // ============================================================================

  const refreshGroupListsAndTables = useCallback(async (groupId) => {
    if (!groupId) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Find lists for this group
    const groupLists = (savedLists || []).filter(l =>
      l.groupId === groupId || l.group_id === groupId
    );

    // Find tables for this group
    const groupTables = (savedTables || []).filter(t =>
      t.groupId === groupId || t.group_id === groupId
    );

    console.log('[Refresh] groupId:', groupId, 'Found lists:', groupLists.length, 'Found tables:', groupTables.length);
    console.log('[Refresh] All tables:', savedTables?.map(t => ({ id: t.id, name: t.name, groupId: t.groupId, group_id: t.group_id })));

    // Refresh each list
    for (const list of groupLists) {
      try {
        const response = await fetch('/api/lists/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            listId: list.id,
            mode: 'real',
            organizationPrompt: list.organizationPrompt || list.organization_prompt,
            listName: list.name,
            currentGroups: list.groups,
            groupId: groupId,
            forceRegenerate: false  // Don't force full regeneration - just add new members to existing groups
          })
        });

        if (response.ok) {
          const data = await response.json();
          const newGroups = data.groups || data.list?.groups;
          if (newGroups && setLocalLists) {
            setLocalLists(prev => prev.map(l =>
              l.id === list.id ? { ...l, groups: newGroups } : l
            ));
          }
        }
      } catch (err) {
        console.error('Error refreshing list:', list.id, err);
      }
    }

    // Refresh each table
    for (const table of groupTables) {
      try {
        const response = await fetch('/api/tables/refresh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            tableId: table.id,
            mode: 'real'
          })
        });

        if (response.ok) {
          const data = await response.json();
          const updatedTable = data.table;
          if (updatedTable && setLocalTables) {
            setLocalTables(prev => prev.map(t =>
              t.id === table.id ? { ...t, ...updatedTable } : t
            ));
          }
        }
      } catch (err) {
        console.error('Error refreshing table:', table.id, err);
      }
    }
  }, [savedLists, savedTables, setLocalLists, setLocalTables]);

  // ============================================================================
  // MAIN CHAT SUBMISSION
  // ============================================================================

  const handleSubmit = useCallback(async (message, focusedDonor = null) => {
    if (!message.trim()) return;

    // Check if we're waiting for more research context
    if (pendingResearch) {
      await handleResearchWithContext(pendingResearch.entryId, pendingResearch.name, message);
      return;
    }

    // Check if we're waiting for more info about a newly added person
    const moreInfoEntry = feedEntries.find(e => e.askMoreInfo);
    if (moreInfoEntry) {
      await handleMoreInfoProvided(moreInfoEntry.id, moreInfoEntry.askMoreInfo, message);
      return;
    }

    // Check if user is saying "yes" to a pending suggest_add
    const affirmativeResponses = ['yes', 'yeah', 'yep', 'sure', 'ok', 'okay', 'please', 'do it', 'add them', 'add her', 'add him', 'go ahead'];
    const messageLower = message.trim().toLowerCase();
    if (pendingSuggestAdd && handleConfirmAddRef.current && affirmativeResponses.some(r => messageLower === r || messageLower.startsWith(r + ' ') || messageLower.startsWith(r + ','))) {
      // User confirmed the pending add - trigger it directly
      const { entryId, name, notes } = pendingSuggestAdd;
      setPendingSuggestAdd(null);
      await handleConfirmAddRef.current(entryId, name, notes);
      return;
    }

    // Check if user is saying "no" to a pending suggest_add
    const negativeResponses = ['no', 'nope', 'nah', 'nevermind', 'never mind', 'skip', 'cancel', 'don\'t'];
    if (pendingSuggestAdd && negativeResponses.some(r => messageLower === r || messageLower.startsWith(r + ' '))) {
      // User declined - clear the pending suggestion
      setPendingSuggestAdd(null);
      // Continue with normal chat flow so they get a response
    }

    setIsSubmitting(true);

    const now = Date.now();
    const questionId = crypto.randomUUID ? crypto.randomUUID() : `q-${now}`;
    const answerId = crypto.randomUUID ? crypto.randomUUID() : `a-${now}`;

    // Add user message to feed
    const questionEntry = {
      id: questionId,
      timestamp: new Date(now).toISOString(),
      type: 'question',
      message: message,
      status: 'sent'
    };

    // Add placeholder for AI response
    const answerEntry = {
      id: answerId,
      timestamp: new Date(now + 1).toISOString(),
      type: 'answer',
      message: '',
      status: 'thinking'
    };

    setFeedEntries(prev => [answerEntry, questionEntry, ...prev]);
    saveFeedEntryToDb(questionEntry);

    // Local Storage Mode: a separate, simpler path - one non-streaming call to
    // the local chat bundle instead of the full Supabase-oriented /api/chat
    // flow below (which this branch never reaches). Keeps the existing flow
    // completely untouched for everyone not using Local Mode.
    if (isLocalModeEnabled()) {
      try {
        const recentConversation = feedEntries
          .filter(e => e.type === 'question' || e.type === 'answer')
          .slice(0, 20)
          .reverse()
          .map(e => ({
            role: e.type === 'question' ? 'user' : 'assistant',
            content: e.message
          }))
          .filter(m => m.content);

        const response = await authPost('/api/local/chat', {
          message,
          localDonors: getLocalDonors(),
          conversationHistory: recentConversation.slice(-10)
        });
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Something went wrong');
        }

        if (data.mutation?.type === 'add') {
          addLocalDonor(data.mutation.donor);
          onRefreshDonors?.();
        } else if (data.mutation?.type === 'update') {
          updateLocalDonor(data.mutation.name, data.mutation.updates);
          onRefreshDonors?.();
          // Local mode has no SSE stream, so fire the same narrativeUpdate signal
          // the Supabase path sends via 'narrative_update' - drives the profile's
          // live rewrite animation instead of only showing up after a refresh.
          if (data.mutation.updates?.narrative) {
            setNarrativeUpdate({
              personName: data.mutation.name,
              narrative: data.mutation.updates.narrative
            });
            setTimeout(() => setNarrativeUpdate(null), 5000);
          }
        }

        const updatedAnswer = { ...answerEntry, message: data.message || 'Got it.', status: 'received' };
        setFeedEntries(prev => prev.map(e => e.id === answerId ? updatedAnswer : e));
        saveFeedEntryToDb(updatedAnswer);
      } catch (err) {
        console.error('Local chat error:', err);
        const errorAnswer = { ...answerEntry, message: "Sorry, something went wrong. Please try again.", status: 'received' };
        setFeedEntries(prev => prev.map(e => e.id === answerId ? errorAnswer : e));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    try {
      // Build conversation history
      const recentConversation = feedEntries
        .filter(e => e.type === 'question' || e.type === 'answer')
        .slice(0, 20)
        .reverse()
        .map(e => ({
          role: e.type === 'question' ? 'user' : 'assistant',
          content: e.message
        }))
        .filter(m => m.content);

      // Send to unified chat endpoint - Claude decides what to do
      const response = await authFetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          conversationHistory: recentConversation.slice(-10),
          userContext: userContext || null // Pass cached user context to avoid extra DB fetch
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalData = null;
      let pendingNarrativeUpdate = null;
      let buffer = '';

      // Set streaming entry ID so DesktopFeed can trigger word reveal when streaming ends
      setStreamingEntryId(answerEntry.id);

      // Stream the response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // A single SSE event can be split across two reads by the underlying TCP
        // stream - buffer any incomplete trailing line instead of dropping it.
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                // Chunks contain raw JSON, we only need the final parsed message
              } else if (data.type === 'token') {
                // Word-by-word streaming (used for onboarding) - update streaming text
                setStreamingText(data.text || '');
                setStreamingEntryId(answerEntry.id);
              } else if (data.type === 'done') {
                finalData = data;
              } else if (data.type === 'narrative_update') {
                // Narrative update arrived from background processing - trigger UI update immediately
                pendingNarrativeUpdate = {
                  personName: data.personName,
                  narrative: data.narrative
                };
                setNarrativeUpdate(pendingNarrativeUpdate);
                // Clear after animation
                setTimeout(() => setNarrativeUpdate(null), 5000);
              } else if (data.type === 'details_update') {
                // Key details and tasks update - merge into narrative update
                setNarrativeUpdate(prev => ({
                  ...prev,
                  personName: data.personName,
                  keyDetails: data.keyDetails,
                  tasks: data.tasks
                }));
              } else if (data.type === 'error') {
                throw new Error(data.message || 'Something went wrong');
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      // Process final response
      const aiMessage = finalData?.message || 'Done.';
      const action = finalData?.action;
      const actionResult = finalData?.actionResult;

      // Build the updated answer entry
      const addedPerson = action?.type === 'add_person' && actionResult?.success;
      const hasGroups = actionResult?.groups?.length > 0;

      // Check if a list was created
      const listCreated = actionResult?.listCreated;

      // Check if we should auto-research (added person with no groups to select, and not showing create group prompt)
      const shouldAutoResearch = addedPerson && !hasGroups && !actionResult?.needsGroupCreation;

      // If needs group creation, open AddPersonModal instead of inline prompt
      if (actionResult?.needsGroupCreation && onOpenAddPersonModal) {
        onOpenAddPersonModal(actionResult.forPerson, '', null);
      }

      const updatedAnswer = {
        ...answerEntry,
        message: aiMessage,
        status: 'received',
        // Step 1: Group selection (if groups exist)
        groupSelection: (addedPerson && hasGroups) ? {
          donorName: actionResult.personName,
          donorId: actionResult.personId,
          groups: actionResult.groups
        } : null,
        // No longer using inline prompt - using AddPersonModal instead
        promptCreateGroup: null,
        // Auto-research triggers after entry update (no askResearch prompt)
        // Show person card if we added someone or are showing someone
        people: addedPerson ? [{
          name: actionResult.personName,
          reason: 'Tap to view profile',
          isNew: true
        }] : (actionResult?.showPerson ? [{
          name: actionResult.showPerson,
          reason: 'Tap to view profile'
        }] : null),
        // List preview if a list was created
        listPreview: listCreated ? {
          listId: actionResult.listId,
          listName: actionResult.listName,
          listPrompt: actionResult.listPrompt,
          groups: actionResult.groups
        } : null,
        // Group selection for list creation
        listGroupSelection: actionResult?.needsGroupForList ? {
          listPrompt: actionResult.listPrompt,
          suggestedName: actionResult.suggestedName,
          groups: actionResult.groups
        } : null
      };

      // Add captured items to the answer entry
      const answerWithCaptured = actionResult?.captured
        ? { ...updatedAnswer, captured: actionResult.captured }
        : updatedAnswer;

      setFeedEntries(prev => prev.map(entry =>
        entry.id === answerEntry.id ? answerWithCaptured : entry
      ));
      saveFeedEntryToDb(answerWithCaptured);

      // Trigger auto-research for newly added person (if no group selection needed)
      if (shouldAutoResearch) {
        performAutoResearch(answerEntry.id, actionResult.personName, actionResult.personId);
      }

      // Refresh user context if onboarding context was just saved
      if (actionResult?.onboardingContextSaved && onRefreshUserContext) {
        onRefreshUserContext();
      }

      // Clear streaming state
      setStreamingText('');
      setStreamingEntryId(null);

      // Refresh donors if we made changes
      if (action?.type === 'add_person' || action?.type === 'update_person') {
        if (onRefreshDonors) {
          setTimeout(() => onRefreshDonors(), 500);
        }
        // Note: narrativeUpdate is now handled via SSE 'narrative_update' event (faster)
        // Only fall back to actionResult if we didn't get it via SSE
        if (action?.type === 'update_person' && actionResult?.updatedNarrative && !pendingNarrativeUpdate) {
          setNarrativeUpdate({
            personName: actionResult.personName,
            narrative: actionResult.updatedNarrative
          });
          setTimeout(() => setNarrativeUpdate(null), 5000);
        }
        // Refresh lists/tables for the person's group
        const personName = actionResult?.personName;
        if (personName) {
          const personData = donors?.[personName];
          const groupId = personData?.group_id || personData?.groupId;
          if (groupId) {
            setTimeout(() => refreshGroupListsAndTables(groupId), 800);
          }
        }
      }

      // NOTE: Removed auto-navigation to person profile
      // Users should click on person cards to navigate, not auto-redirect

      // Track suggest_add for text-based "yes" confirmation
      if (action?.type === 'suggest_add') {
        const name = action.data?.name || '';
        const notes = action.data?.notes || action.data?.note || '';
        setPendingSuggestAdd({ entryId: answerEntry.id, name, notes });

        // Also open modal if available
        if (onOpenAddPersonModal) {
          setTimeout(() => {
            onOpenAddPersonModal(name, notes, null);
          }, 300);
        }
      } else if (action?.type === 'open_add_modal' && onOpenAddPersonModal) {
        const name = action.data?.name || '';
        const notes = action.data?.notes || action.data?.note || '';
        setTimeout(() => {
          onOpenAddPersonModal(name, notes, null);
        }, 300);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === answerEntry.id
          ? { ...entry, message: 'Sorry, something went wrong. Please try again.', status: 'error' }
          : entry
      ));
      setStreamingText('');
      setStreamingEntryId(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [feedEntries, pendingResearch, donors, onRefreshDonors, onNavigateToPerson, onShowCreateGroupSheet, saveFeedEntryToDb, refreshGroupListsAndTables, userContext]);

  // ============================================================================
  // AUTO-RESEARCH FUNCTION
  // ============================================================================

  // Auto-research a newly added person and update their profile
  const performAutoResearch = useCallback(async (entryId, name, donorId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Show researching state
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              askResearch: null,
              researching: { name }
            }
          : entry
      ));

      // Call the research-person API
      const response = await authFetch('/api/research-person', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personName: name })
      });

      if (!response.ok) throw new Error('Research failed');

      const result = await response.json();

      // Handle multiple matches - show cards to pick
      if (result.matches && result.matches.length > 1) {
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                researching: null,
                message: `Found ${result.matches.length} possible matches for ${name}:`,
                researchOptions: {
                  donorName: name,
                  donorId: donorId,
                  options: result.matches.map(m => ({
                    title: m.name || name,
                    description: m.summary || m.info?.substring(0, 100),
                    fullInfo: m.info
                  }))
                }
              }
            : entry
        ));
        return;
      }

      // Handle single match - save the info
      if (result.matches && result.matches.length === 1) {
        const info = result.matches[0].info;

        // Update the person's narrative with research info
        const { data: donorData } = await supabase
          .from('donors')
          .select('narrative')
          .eq('user_id', session.user.id)
          .eq('name', name)
          .single();

        const currentNarrative = donorData?.narrative || '';
        const updatedNarrative = currentNarrative
          ? `${currentNarrative}\n\n${info}`
          : info;

        await supabase
          .from('donors')
          .update({ narrative: updatedNarrative })
          .eq('user_id', session.user.id)
          .eq('name', name);

        // Pre-generate timeline in background (so it's ready when user views profile)
        authFetch('/api/donor-timeline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ donorName: name, forceRefresh: true })
        }).catch(() => {}); // Fire and forget

        // Update feed entry with success
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                researching: null,
                message: `Added ${name} with research!`,
                donorCard: { name, donorId }
              }
            : entry
        ));

        // Show narrative update notification
        setNarrativeUpdate({ personName: name, narrative: updatedNarrative });
        setTimeout(() => setNarrativeUpdate(null), 5000);

        if (onRefreshDonors) setTimeout(() => onRefreshDonors(), 300);
        return;
      }

      // Handle notFound or no useful result - just complete without research
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              researching: null,
              message: `Added ${name}!`,
              donorCard: { name, donorId }
            }
          : entry
      ));

    } catch (error) {
      console.error('Auto-research error:', error);
      // On error, just complete without research
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              researching: null,
              message: `Added ${name}!`,
              donorCard: { name, donorId }
            }
          : entry
      ));
    }
  }, [onRefreshDonors]);

  // ============================================================================
  // GROUP SELECTION HANDLERS
  // ============================================================================

  const handleGroupSelect = useCallback(async (entryId, donorId, donorName, groupId, groupName) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Handle "new group" option
      if (groupId === 'new') {
        const newGroupName = prompt('New group name:');
        if (!newGroupName?.trim()) return;

        // Create the new group
        const { data: newGroup, error: createError } = await supabase
          .from('groups')
          .insert({
            user_id: session.user.id,
            name: newGroupName.trim(),
            color: '#a78bfa'
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating group:', createError);
          return;
        }

        // Update donor with new group
        if (donorId) {
          await supabase
            .from('donors')
            .update({ group_id: newGroup.id })
            .eq('id', donorId);
        }

        // Update local groups
        if (setLocalGroups) {
          setLocalGroups(prev => [...prev, newGroup]);
        }

        // Update feed entry - clear group selection
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                message: `Added ${donorName} to ${newGroupName}.`,
                groupSelection: null
              }
            : entry
        ));

        // Trigger auto-research for the newly added person
        performAutoResearch(entryId, donorName, donorId);

        if (onRefreshDonors) setTimeout(() => onRefreshDonors(), 300);
        return;
      }

      // Update the donor's group in Supabase
      if (session && donorId) {
        const { error } = await supabase
          .from('donors')
          .update({ group_id: groupId })
          .eq('id', donorId);

        if (error) {
          console.error('Error updating donor group:', error);
        } else {
          console.log('Added', donorName, 'to group:', groupName);
        }
      }

      // Update the feed entry - clear group selection
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              message: `Added ${donorName} to ${groupName}.`,
              groupSelection: null
            }
          : entry
      ));

      // Trigger auto-research for the newly added person
      performAutoResearch(entryId, donorName, donorId);

      // Refresh donors list after group assignment
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }
    } catch (err) {
      console.error('Error selecting group:', err);
    }
  }, [onRefreshDonors, setLocalGroups, performAutoResearch]);

  const handleSkipGroupSelect = useCallback((entryId, donorName, donorId) => {
    // Update original entry
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? {
            ...entry,
            message: `Added ${donorName}.`,
            groupSelection: null
          }
        : entry
    ));

    // Trigger auto-research for the newly added person
    performAutoResearch(entryId, donorName, donorId);
  }, [performAutoResearch]);

  // ============================================================================
  // CREATE GROUP FROM FEED
  // ============================================================================

  const handleCreateGroupFromFeed = useCallback(async (entryId, suggestedName, forPerson) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // If suggestedName is null, prompt for a name
      let groupName = suggestedName;
      if (!groupName) {
        groupName = prompt('Enter a name for your new group:');
        if (!groupName?.trim()) return;
        groupName = groupName.trim();
      }

      // Create the group
      const { data: newGroup, error: createError } = await supabase
        .from('groups')
        .insert({
          user_id: session.user.id,
          name: groupName,
          color: '#a78bfa'
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating group:', createError);
        return;
      }

      // Add to local groups
      if (setLocalGroups) {
        setLocalGroups(prev => [...prev, newGroup]);
      }

      // If there's a person to assign, find them and assign to this group
      if (forPerson) {
        const { data: donor } = await supabase
          .from('donors')
          .select('id')
          .eq('user_id', session.user.id)
          .eq('name', forPerson)
          .single();

        if (donor) {
          await supabase
            .from('donors')
            .update({ group_id: newGroup.id })
            .eq('id', donor.id);
        }

        // Update feed entry
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                message: `Created "${groupName}" and added ${forPerson}.`,
                promptCreateGroup: null
              }
            : entry
        ));

        // Trigger auto-research for the newly added person
        if (donor?.id) {
          performAutoResearch(entryId, forPerson, donor.id);
        }
      } else {
        // Just update the feed entry
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                message: `Created "${groupName}".`,
                promptCreateGroup: null
              }
            : entry
        ));
      }

      if (onRefreshDonors) setTimeout(() => onRefreshDonors(), 300);
    } catch (error) {
      console.error('Error creating group from feed:', error);
    }
  }, [onRefreshDonors, setLocalGroups, performAutoResearch]);

  // ============================================================================
  // LIST CREATION HANDLERS
  // ============================================================================

  const handleViewList = useCallback((listPreview) => {
    if (onNavigateToList) {
      onNavigateToList(listPreview);
    }
  }, [onNavigateToList]);

  const handleCreateListForGroup = useCallback(async (entryId, group, listPrompt, suggestedName) => {
    // Update entry to show loading
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, listGroupSelection: null, message: `Creating list for ${group.name}...`, status: 'streaming' }
        : entry
    ));

    try {
      const response = await authPost('/api/create-list-for-group', {
        groupId: group.id,
        groupName: group.name,
        listPrompt,
        suggestedName,
        mode: 'real'
      });

      if (!response.ok) throw new Error('Failed to create list');

      const data = await response.json();

      // Create the new list object
      const newList = {
        id: data.listId,
        name: data.listName,
        organizationPrompt: listPrompt,
        groups: data.groups,
        group_id: group.id
      };

      // Add the new list to localLists immediately
      if (setLocalLists) {
        setLocalLists(prev => [newList, ...prev]);
      }

      // Update entry with the list preview
      const updatedEntry = {
        id: entryId,
        message: `Created "${data.listName}" for ${group.name}`,
        status: 'received',
        type: 'answer',
        listPreview: {
          listId: data.listId,
          listName: data.listName,
          listPrompt: listPrompt,
          groups: data.groups,
          groupId: group.id,
          groupName: group.name
        }
      };

      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? { ...entry, ...updatedEntry }
          : entry
      ));

      // Save the updated entry with listPreview to the database
      saveFeedEntryToDb(updatedEntry);

      // Navigate directly to the list
      if (onNavigateToList) {
        onNavigateToList({
          listId: data.listId,
          listName: data.listName,
          listPrompt: listPrompt,
          groups: data.groups,
          groupId: group.id,
          groupName: group.name
        });
      }

      // Refresh to pick up any other changes
      if (onRefreshDonors) onRefreshDonors();

    } catch (error) {
      console.error('Error creating list:', error);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? { ...entry, message: 'Failed to create list. Please try again.', status: 'error' }
          : entry
      ));
    }
  }, [onRefreshDonors, onNavigateToList, setLocalLists, saveFeedEntryToDb]);

  // ============================================================================
  // CONFIRM/DECLINE ADD HANDLERS
  // ============================================================================

  const handleConfirmAdd = useCallback(async (entryId, name, notes) => {
    // Clear the suggest buttons and show a loading state
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, suggestAdd: null, message: `Adding ${name}...` }
        : entry
    ));

    try {
      // Call the chat API with a "yes" to trigger confirm_add
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          message: `yes, add ${name}`,
          mode: 'real',
          conversationHistory: [{
            role: 'assistant',
            content: `I don't see ${name} in your contacts. Want me to add them?`
          }]
        })
      });

      const data = await response.json();

      // Check if person was added
      const isConfirmAdd = data.action?.type === 'confirm_add' || data.action?.type === 'add_donor';
      const donorId = data.action?.data?.donorId;

      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              message: data.response || `Added ${name}!`,
              suggestAdd: null
            }
          : entry
      ));

      // Trigger auto-research for the newly added person
      if (isConfirmAdd && donorId) {
        performAutoResearch(entryId, name, donorId);
      }

      if (isConfirmAdd && onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 500);
      }
    } catch (err) {
      console.error('Error confirming add:', err);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? { ...entry, message: `Failed to add ${name}.`, suggestAdd: null }
          : entry
      ));
    }
  }, [onRefreshDonors, performAutoResearch]);

  // Keep ref updated for use in handleSubmit
  handleConfirmAddRef.current = handleConfirmAdd;

  const handleDeclineAdd = useCallback((entryId) => {
    // Clear pending research if declining
    setPendingResearch(null);
    setPendingMoreInfo(null);
    setPendingSuggestAdd(null); // Also clear pending suggestion
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, suggestAdd: null, askResearch: null, askMoreInfo: null }
        : entry
    ));
  }, []);

  // ============================================================================
  // RESEARCH HANDLERS
  // ============================================================================

  const handleResearchPerson = useCallback((entryId, name) => {
    // Ask for more context before researching
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? {
            ...entry,
            askResearch: null,
            status: 'received',
            message: `What can you tell me about ${name}? Any details like their company, role, or how you know them will help me research them better.`,
            askMoreInfo: { name, entryId }
          }
        : entry
    ));
  }, []);

  const handleSkipResearch = useCallback((entryId, name, donorId) => {
    // Clear the research prompt and show a done message with profile link
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? {
            ...entry,
            askResearch: null,
            message: `Added ${name}!`,
            donorCard: { name, donorId }
          }
        : entry
    ));
  }, []);

  const handleSkipMoreInfo = useCallback((entryId, name, donorId) => {
    setPendingMoreInfo(null);
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? {
            ...entry,
            askMoreInfo: null
          }
        : entry
    ));
  }, []);

  // Handle when user provides more info about a newly added person - update then auto-research
  const handleMoreInfoProvided = useCallback(async (entryId, moreInfo, userMessage) => {
    const { name, donorId } = moreInfo;
    setIsSubmitting(true);

    // Clear the askMoreInfo from the original entry
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, askMoreInfo: null, message: '' }
        : entry
    ));

    // Add user message to feed
    const now = Date.now();
    const questionId = crypto.randomUUID ? crypto.randomUUID() : `q-${now}`;
    const questionEntry = {
      id: questionId,
      timestamp: new Date(now).toISOString(),
      type: 'question',
      message: userMessage,
      status: 'sent'
    };

    // Add a NEW answer entry for the research results
    const answerId = crypto.randomUUID ? crypto.randomUUID() : `a-${now}`;
    const answerEntry = {
      id: answerId,
      timestamp: new Date(now + 1).toISOString(),
      type: 'answer',
      message: `Researching ${name}...`,
      status: 'thinking'
    };

    setFeedEntries(prev => [answerEntry, questionEntry, ...prev]);
    saveFeedEntryToDb(questionEntry);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      // Update the person with the new info via the chat API
      const response = await authPost('/api/chat', {
        message: `Update ${name}'s profile with this info: ${userMessage}`,
        donorData: donors,
        context: { focusedDonor: name }
      });

      if (!response.ok) throw new Error('Failed to update');

      // Refresh donors to get updated data
      if (onRefreshDonors) {
        setTimeout(() => onRefreshDonors(), 300);
      }

      // Auto-research with the context they provided
      const researchResponse = await fetch('/api/donor-insight', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          donorName: name,
          additionalContext: userMessage
        })
      });

      if (researchResponse.ok) {
        const researchData = await researchResponse.json();

        // Check if research found multiple people with this name
        if (researchData.ambiguous && researchData.options?.length > 0) {
          // Show options for user to pick which person
          setFeedEntries(prev => prev.map(entry =>
            entry.id === answerId
              ? {
                  ...entry,
                  message: researchData.message || `Found multiple people named ${name}. Which one?`,
                  status: 'received',
                  researchOptions: {
                    donorName: name,
                    donorId: donorId,
                    options: researchData.options
                  }
                }
              : entry
          ));
          return;
        }

        const insight = researchData.insight;

        if (insight) {
          // Get current donor narrative
          const { data: donorData } = await supabase
            .from('donors')
            .select('narrative')
            .eq('user_id', session.user.id)
            .eq('name', name)
            .single();

          const currentNarrative = donorData?.narrative || '';
          const updatedNarrative = currentNarrative
            ? currentNarrative + '\n\n' + insight
            : insight;

          // Save research to donor profile
          await fetch('/api/donor-update', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              donorName: name,
              field: 'narrative',
              value: updatedNarrative,
              mode: 'real'
            })
          });

          // Extract timeline, key details, and tasks from the narrative
          try {
            await fetch('/api/donor-timeline', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({
                donorName: name,
                mode: 'real',
                forceRefresh: true
              })
            });
          } catch (timelineErr) {
            console.error('Error extracting timeline:', timelineErr);
          }

          // Update the NEW answer entry with research results + person card
          setFeedEntries(prev => prev.map(entry =>
            entry.id === answerId
              ? {
                  ...entry,
                  message: insight,
                  status: 'received',
                  sourceInfo: 'Research saved to profile',
                  people: [{
                    name: name,
                    reason: 'Tap to view profile'
                  }]
                }
              : entry
          ));
        } else {
          // No new info found
          setFeedEntries(prev => prev.map(entry =>
            entry.id === answerId
              ? {
                  ...entry,
                  message: `No new info found for ${name}.`,
                  status: 'received',
                  people: [{
                    name: name,
                    reason: 'Tap to view profile'
                  }]
                }
              : entry
          ));
        }

        // Refresh to get updated profile
        if (onRefreshDonors) {
          setTimeout(() => onRefreshDonors(), 500);
        }

        // Refresh lists/tables for the person's group
        const personData = donors?.[name];
        const groupId = personData?.group_id || personData?.groupId;
        if (groupId) {
          setTimeout(() => refreshGroupListsAndTables(groupId), 800);
        }
      } else {
        // Research failed, just show done with person card
        setFeedEntries(prev => prev.map(entry =>
          entry.id === answerId
            ? {
                ...entry,
                message: `Updated ${name}'s profile.`,
                status: 'received',
                people: [{
                  name: name,
                  reason: 'Tap to view profile'
                }]
              }
            : entry
        ));
      }
    } catch (error) {
      console.error('Error updating person:', error);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === answerId
          ? {
              ...entry,
              message: `Added ${name}.`,
              status: 'received'
            }
          : entry
      ));
    } finally {
      setIsSubmitting(false);
    }
  }, [donors, onRefreshDonors, refreshGroupListsAndTables, saveFeedEntryToDb]);

  // Handle research with additional context provided by user
  const handleResearchWithContext = useCallback(async (originalEntryId, name, additionalContext) => {
    // Clear pending research state
    setPendingResearch(null);
    setIsSubmitting(true);

    const now = Date.now();
    const questionId = crypto.randomUUID ? crypto.randomUUID() : `q-${now}`;
    const answerId = crypto.randomUUID ? crypto.randomUUID() : `a-${now}`;

    // Show user's context as a question
    const questionEntry = {
      id: questionId,
      timestamp: new Date(now).toISOString(),
      type: 'question',
      message: additionalContext,
      status: 'sent'
    };

    // Show thinking state for research
    const answerEntry = {
      id: answerId,
      timestamp: new Date(now + 1).toISOString(),
      type: 'answer',
      message: `Researching ${name} with that context...`,
      status: 'thinking'
    };

    setFeedEntries(prev => [answerEntry, questionEntry, ...prev]);
    saveFeedEntryToDb(questionEntry);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsSubmitting(false);
        return;
      }

      // First, save the context to the donor's narrative so the research API can use it
      const { data: donorData } = await supabase
        .from('donors')
        .select('narrative')
        .eq('user_id', session.user.id)
        .eq('name', name)
        .single();

      const currentNarrative = donorData?.narrative || '';
      const narrativeWithContext = currentNarrative
        ? currentNarrative + '\n\n' + additionalContext
        : additionalContext;

      // Update narrative with context first
      await fetch('/api/donor-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          donorName: name,
          field: 'narrative',
          value: narrativeWithContext,
          mode: 'real'
        })
      });

      // Now call research API - it will use the updated narrative for context
      const response = await fetch('/api/donor-insight', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          donorName: name,
          mode: 'real'
        })
      });

      const data = await response.json();

      if (data.insight && data.insight !== null) {
        // Append research results to narrative
        const finalNarrative = narrativeWithContext + '\n\n' + data.insight;

        await fetch('/api/donor-update', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            donorName: name,
            field: 'narrative',
            value: finalNarrative,
            mode: 'real'
          })
        });

        // Update feed with results
        setFeedEntries(prev => prev.map(entry =>
          entry.id === answerId
            ? {
                ...entry,
                status: 'received',
                message: data.insight,
                sourceInfo: 'Research saved to profile'
              }
            : entry
        ));

        if (onRefreshDonors) {
          setTimeout(() => onRefreshDonors(), 500);
        }

        // Refresh lists/tables for the person's group
        const personData = donors?.[name];
        const groupId = personData?.group_id || personData?.groupId;
        if (groupId) {
          setTimeout(() => refreshGroupListsAndTables(groupId), 800);
        }
      } else {
        // Still no results - context was saved at least
        setFeedEntries(prev => prev.map(entry =>
          entry.id === answerId
            ? {
                ...entry,
                status: 'received',
                message: `I still couldn't find public information about ${name}, but I've saved your notes to their profile.`,
                sourceInfo: 'Notes saved to profile'
              }
            : entry
        ));

        if (onRefreshDonors) {
          setTimeout(() => onRefreshDonors(), 500);
        }

        // Refresh lists/tables for the person's group
        const personData = donors?.[name];
        const groupId = personData?.group_id || personData?.groupId;
        if (groupId) {
          setTimeout(() => refreshGroupListsAndTables(groupId), 800);
        }
      }
    } catch (err) {
      console.error('Error researching with context:', err);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === answerId
          ? {
              ...entry,
              status: 'received',
              message: `Sorry, I had trouble researching ${name}. Your notes have been saved to their profile.`
            }
          : entry
      ));
    } finally {
      setIsSubmitting(false);
    }
  }, [donors, onRefreshDonors, refreshGroupListsAndTables, saveFeedEntryToDb]);

  // Handle selecting a specific person from ambiguous research options
  const handleResearchOptionSelect = useCallback(async (entryId, donorName, donorId, selectedOption) => {
    // Show thinking state
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? {
            ...entry,
            researchOptions: null,
            message: `Researching ${selectedOption.name}...`,
            status: 'thinking'
          }
        : entry
    ));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Call research API with the selected option
      const response = await fetch('/api/donor-insight', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          donorName: donorName,
          selectedOption: `${selectedOption.name} - ${selectedOption.description}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        const insight = data.insight;

        if (insight) {
          // Get current donor narrative
          const { data: donorData } = await supabase
            .from('donors')
            .select('narrative')
            .eq('user_id', session.user.id)
            .eq('name', donorName)
            .single();

          const currentNarrative = donorData?.narrative || '';
          const updatedNarrative = currentNarrative
            ? currentNarrative + '\n\n' + insight
            : insight;

          // Save research to donor profile
          await fetch('/api/donor-update', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              donorName: donorName,
              field: 'narrative',
              value: updatedNarrative,
              mode: 'real'
            })
          });

          // Update entry with results
          setFeedEntries(prev => prev.map(entry =>
            entry.id === entryId
              ? {
                  ...entry,
                  message: insight,
                  status: 'received',
                  sourceInfo: 'Research saved to profile',
                  people: [{
                    name: donorName,
                    reason: 'Tap to view profile'
                  }]
                }
              : entry
          ));

          if (onRefreshDonors) {
            setTimeout(() => onRefreshDonors(), 500);
          }

          // Refresh lists/tables for the person's group
          const personData = donors?.[donorName];
          const groupId = personData?.group_id || personData?.groupId;
          if (groupId) {
            setTimeout(() => refreshGroupListsAndTables(groupId), 800);
          }
        } else {
          setFeedEntries(prev => prev.map(entry =>
            entry.id === entryId
              ? {
                  ...entry,
                  message: `No additional information found for ${selectedOption.name}.`,
                  status: 'received',
                  people: [{
                    name: donorName,
                    reason: 'Tap to view profile'
                  }]
                }
              : entry
          ));
        }
      } else {
        throw new Error('Research failed');
      }
    } catch (error) {
      console.error('Error researching selected option:', error);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              message: 'Sorry, something went wrong. Please try again.',
              status: 'received'
            }
          : entry
      ));
    }
  }, [donors, onRefreshDonors, refreshGroupListsAndTables]);

  // Handle accepting the offer to research externally
  const handleAcceptResearch = useCallback(async (entryId, query, context) => {
    // Update the entry to show we're researching
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, offerResearch: null, status: 'thinking', message: 'Looking this up...' }
        : entry
    ));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Extract any person name from the query/context for research
      // The query might be about a specific person - try to find them
      const donorNames = Object.keys(donors || {});
      const queryLower = query.toLowerCase();
      const matchedDonor = donorNames.find(name => {
        const nameLower = name.toLowerCase();
        const firstName = nameLower.split(' ')[0];
        return queryLower.includes(nameLower) || queryLower.includes(firstName);
      });

      if (matchedDonor) {
        // Research a specific person
        const response = await fetch('/api/donor-insight', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            donorName: matchedDonor,
            mode: 'real',
            specificQuery: query
          })
        });

        const data = await response.json();

        if (data.insight) {
          // Save to donor profile
          const { data: donorData } = await supabase
            .from('donors')
            .select('narrative')
            .eq('user_id', session.user.id)
            .eq('name', matchedDonor)
            .single();

          const currentNarrative = donorData?.narrative || '';
          const updatedNarrative = currentNarrative
            ? currentNarrative + '\n\n' + data.insight
            : data.insight;

          await fetch('/api/donor-update', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              donorName: matchedDonor,
              field: 'narrative',
              value: updatedNarrative,
              mode: 'real'
            })
          });

          setFeedEntries(prev => prev.map(entry =>
            entry.id === entryId
              ? {
                  ...entry,
                  status: 'received',
                  message: data.insight,
                  sourceInfo: `Research saved to ${matchedDonor}'s profile`
                }
              : entry
          ));

          if (onRefreshDonors) {
            setTimeout(() => onRefreshDonors(), 500);
          }
        } else {
          setFeedEntries(prev => prev.map(entry =>
            entry.id === entryId
              ? {
                  ...entry,
                  status: 'received',
                  message: `I couldn't find specific information about that for ${matchedDonor}.`
                }
              : entry
          ));
        }
      } else {
        // General query - no specific person found
        setFeedEntries(prev => prev.map(entry =>
          entry.id === entryId
            ? {
                ...entry,
                status: 'received',
                message: "I couldn't identify which person you're asking about. Try mentioning their name in your question."
              }
            : entry
        ));
      }
    } catch (err) {
      console.error('Error with external research:', err);
      setFeedEntries(prev => prev.map(entry =>
        entry.id === entryId
          ? {
              ...entry,
              status: 'received',
              message: 'Sorry, I had trouble looking that up. Please try again.'
            }
          : entry
      ));
    }
  }, [donors, onRefreshDonors]);

  const handleDeclineResearch = useCallback((entryId) => {
    setFeedEntries(prev => prev.map(entry =>
      entry.id === entryId
        ? { ...entry, offerResearch: null }
        : entry
    ));
  }, []);

  // ============================================================================
  // NOTES FROM PROFILE
  // ============================================================================

  const handleAddNoteFromProfile = useCallback(async (message, donorName) => {
    // Prepend the donor name to help Claude understand who the note is about
    const contextualMessage = donorName ? `About ${donorName}: ${message}` : message;
    await handleSubmit(contextualMessage);
  }, [handleSubmit]);

  // Return all state and handlers
  return {
    // State
    feedEntries,
    setFeedEntries,
    feedLoading,
    isSubmitting,
    streamingText,
    streamingEntryId,
    narrativeUpdate,
    setNarrativeUpdate,

    // Core handlers
    handleSubmit,
    handleAddNoteFromProfile,
    saveFeedEntryToDb,
    refreshGroupListsAndTables,

    // Group selection
    handleGroupSelect,
    handleSkipGroupSelect,
    handleCreateGroupFromFeed,

    // List handlers
    handleViewList,
    handleCreateListForGroup,

    // Confirm/decline
    handleConfirmAdd,
    handleDeclineAdd,

    // Research handlers
    handleResearchPerson,
    handleSkipResearch,
    handleSkipMoreInfo,
    handleMoreInfoProvided,
    handleResearchWithContext,
    handleResearchOptionSelect,
    handleAcceptResearch,
    handleDeclineResearch
  };
}

export default useChatLogic;
